import { useCallback, useEffect, useRef, useState } from 'react';
import { sendAiChatMessage } from '../../../services/api/AiChatService';

const HINT_BY_STATUS = {
  initial: 'Shift+Enterで改行',
  clarification: '上記の質問にお答えください',
  awaiting_response: 'AIが回答を生成しています…',
  answered: '回答が完了しました',
  error: '新しい質問を始めてください',
};

const CONTEXT_MISSING_HINT = '文書情報を読み込めなかったため、AIチャットを利用できません。';

// パイプラインのノード名 → ユーザー向け進捗ラベル（services/ai_chat/pipeline.py の _build_graph のノード構成に対応）
const PROGRESS_LABEL_BY_NODE = {
  receive_query: '質問を受け付けています…',
  screen_query: '質問内容を検査しています…',
  interpret_query: '質問を解析しています…',
  ask_clarification: '確認内容を整理しています…',
  build_search_queries: '検索クエリを作成しています…',
  run_kb_search: '文書を検索しています…',
  assess_results: '検索結果を確認しています…',
  rewrite_queries: '検索条件を調整しています…',
  generate_answer: '回答を作成しています…',
};

/**
 * 検索結果詳細画面「AIに聞く」パネルの状態・送受信ロジックを管理するカスタムフック
 *
 * 会話は「質問→(聞き返し)→回答→リセット」の1サイクルのみを扱う（MVPスコープ）。
 * SSEイベント（progress/clarification/clarification_delta/answer/answer_delta/error）を
 * AiChatServiceから受け取り、状態（status/progressLabel）とメッセージ履歴（messages）に変換する。
 * answer_delta/clarification_deltaは末尾のstreaming中AIメッセージへ逐次追記し、
 * 確定イベント（answer/clarification）で最終テキストに置き換える。
 *
 * @param {Object} params
 * @param {string} params.blobName - 表示中文書のBlob名
 * @param {string} params.indexName - 検索対象のインデックス名
 * @param {number} params.insuranceType - 保険種別（1:生保, 2:損保）
 * @param {string} [params.documentType] - 文書種別（例: "しおり・約款"。ドメインコンテキスト選択に使用）
 * @returns {Object} チャットパネル用の状態とハンドラ
 */
export const useAiChat = ({ blobName, indexName, insuranceType, documentType }) => {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('initial');
  const [errorMessage, setErrorMessage] = useState('');
  const [progressLabel, setProgressLabel] = useState('');
  const conversationIdRef = useRef(null);
  const abortControllerRef = useRef(null);
  // 遷移元によっては index_name/blobName が渡ってこないケースがあるため、
  // その場合は送信不可にしてバックエンドへの無効なリクエストを未然に防ぐ。
  const isContextMissing = !blobName || !indexName;

  /**
   * ストリーミング中のAIメッセージ（answer_delta/clarification_delta）に追記するためのヘルパー。
   * 直前のメッセージが同種のstreaming中メッセージならcontentを追記し、なければ新規追加する。
   * @param {string} content - 追記する差分テキスト
   * @param {boolean} isClarification - 聞き返し文の差分かどうか（answer_deltaの場合はfalse）
   */
  const appendStreamingDelta = useCallback((content, isClarification) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'ai' && last.streaming && !!last.isClarification === isClarification) {
        const updated = { ...last, content: last.content + content };
        return [...prev.slice(0, -1), updated];
      }
      return [
        ...prev,
        { role: 'ai', content, isClarification, streaming: true, timestamp: Date.now() },
      ];
    });
  }, []);

  /**
   * generate_answer完了（本文は確定済み、attribute_citationsによる出典確定はまだ）を示すため、
   * 末尾のstreaming中AIメッセージにcitationsPendingを立てる。streaming自体は変更しない
   * （hasStreamingMessageの挙動・入力欄ロック・deltaの追記対象判定に影響を与えないため）。
   */
  const markCitationsPending = useCallback(() => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'ai' && last.streaming && !last.isClarification) {
        const updated = { ...last, citationsPending: true };
        return [...prev.slice(0, -1), updated];
      }
      return prev;
    });
  }, []);

  /**
   * ストリーミング中のAIメッセージを確定内容（サーバーから届いた全文）で置き換える。
   * streaming中メッセージが無い場合（deltaが1件も届かなかった場合）はフォールバックとして新規追加する。
   * @param {string} content - 確定した本文（回答全文 または 聞き返し文）
   * @param {Object} [extra] - メッセージにマージする追加フィールド（isClarification/sources等）
   */
  const finalizeStreamingMessage = useCallback((content, extra) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'ai' && last.streaming) {
        const updated = { ...last, content, streaming: false, ...extra };
        return [...prev.slice(0, -1), updated];
      }
      return [...prev, { role: 'ai', content, streaming: false, timestamp: Date.now(), ...extra }];
    });
  }, []);

  /**
   * エラー発生時、未完了のstreamingメッセージが残っていればそのまま確定し、
   * エラーは別のAIメッセージとして新規追加する。
   * ユーザーが既に目にしていた途中経過のテキストは変更せず（errorは付けない）、
   * streamingだけ終了させて通常の完了メッセージとして扱う。
   * streaming中メッセージが無い場合（deltaが1件も届いていない場合）は、
   * エラー専用メッセージのみを追加する。
   */
  const finalizeStreamingMessageAndAppendErrorNotice = useCallback(() => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      const errorMessageEntry = { role: 'ai', content: '', error: true, streaming: false, timestamp: Date.now() };
      if (last && last.role === 'ai' && last.streaming) {
        const finalized = { ...last, streaming: false };
        return [...prev.slice(0, -1), finalized, errorMessageEntry];
      }
      return [...prev, errorMessageEntry];
    });
  }, []);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // blobName（表示中文書）が変わった場合、別文書のコンテキストで会話が継続しないようリセットする。
  // 初回マウント時はリセット不要なため、前回値をrefで追跡して変化した場合のみ実行する。
  const previousBlobNameRef = useRef(blobName);
  useEffect(() => {
    if (previousBlobNameRef.current === blobName) return;
    previousBlobNameRef.current = blobName;

    abortControllerRef.current?.abort();
    conversationIdRef.current = null;
    setMessages([]);
    setStatus('initial');
    setErrorMessage('');
    setProgressLabel('');
  }, [blobName]);

  /**
   * ユーザーの質問（または聞き返しへの回答）をAIチャットへ送信する。
   * SSEイベントは内部のonEventコールバックでstatus/messages/progressLabelへ反映する。
   * @param {string} text - 送信するテキスト（前後空白はtrim後に判定される）
   * @returns {Promise<void>}
   */
  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || status === 'awaiting_response' || isContextMissing) return;

      setMessages((prev) => [...prev, { role: 'user', content: trimmed, timestamp: Date.now() }]);
      setStatus('awaiting_response');
      setErrorMessage('');
      setProgressLabel('');

      const controller = new AbortController();
      abortControllerRef.current = controller;

      // clarification/answer/errorのいずれかが届いたかどうかの追跡。
      // これらが1件も届かずストリームがEOFした場合、状態が'awaiting_response'のまま固まってしまうため、
      // 後続の復帰処理で使う（会話が正常終了扱いのまま入力欄が永久に無効化されるのを防ぐ）。
      let didReceiveTerminalEvent = false;

      try {
        await sendAiChatMessage(
          {
            conversation_id: conversationIdRef.current,
            blob_name: blobName,
            index_name: indexName,
            insurance_type: insuranceType,
            document_type: documentType || '',
            message: trimmed,
          },
          (event) => {
            if (event.event === 'progress') {
              // パイプラインが今どのノードを実行中かの通知。回答本体ではないのでmessagesには追加しない。
              setProgressLabel(PROGRESS_LABEL_BY_NODE[event.node] || '');
              // generate_answer完了 = 本文は確定済み、以降はattribute_citations（出典確定）待ち。
              // 本文のストリーミングは既に終わっているため、末尾メッセージにその旨を記録する。
              if (event.node === 'generate_answer') {
                markCitationsPending();
              }
            } else if (event.event === 'clarification_delta') {
              // 聞き返し文のストリーミング差分。確定前なので末尾のstreamingメッセージに追記していく。
              appendStreamingDelta(event.content, true);
            } else if (event.event === 'answer_delta') {
              // 回答文のストリーミング差分。clarification_deltaと同じ仕組みで追記する。
              appendStreamingDelta(event.content, false);
            } else if (event.event === 'clarification') {
              // 聞き返しの確定イベント。conversation_idを保存し、質問への回答待ち状態にする。
              didReceiveTerminalEvent = true;
              conversationIdRef.current = event.conversation_id;
              finalizeStreamingMessage(event.question, { isClarification: true });
              setProgressLabel('');
              setStatus('clarification');
            } else if (event.event === 'answer') {
              // 回答の確定イベント。引用元（sources）も併せて確定し、1サイクル完了状態にする。
              // sourcesUnavailable=true の場合、バックエンドが誤ってsourcesを返してきても
              // 「出典確認不可の注記」と「引用元チップ」が同時表示されないよう、常に空配列に強制する。
              didReceiveTerminalEvent = true;
              conversationIdRef.current = event.conversation_id;
              const sourcesUnavailable = !!event.sources_unavailable;
              finalizeStreamingMessage(event.content, {
                sources: sourcesUnavailable ? [] : event.sources || [],
                sourcesUnavailable,
                citationsPending: false,
              });
              setProgressLabel('');
              setStatus('answered');
            } else if (event.event === 'error') {
              // サーバー側で処理エラーが発生した場合。未完了のstreamingメッセージは保持したままエラー表示に切り替える。
              didReceiveTerminalEvent = true;
              finalizeStreamingMessageAndAppendErrorNotice();
              setProgressLabel('');
              setErrorMessage(event.message);
              setStatus('error');
            }
          },
          controller.signal
        );

        // ストリームが例外を投げずに終了したにもかかわらず、終端イベントが1件も届かなかった場合
        // （サーバーが接続を切っただけ等）は、'awaiting_response'のまま固まってしまうためエラー扱いに遷移する。
        if (!didReceiveTerminalEvent) {
          finalizeStreamingMessageAndAppendErrorNotice();
          setProgressLabel('');
          setErrorMessage('AIチャットとの通信が予期せず終了しました。再度お試しください。');
          setStatus('error');
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('AIチャット送信エラー:', error);
        finalizeStreamingMessageAndAppendErrorNotice();
        setProgressLabel('');
        setErrorMessage('AIチャットとの通信中にエラーが発生しました。');
        setStatus('error');
      }
    },
    [blobName, indexName, insuranceType, documentType, status, isContextMissing, appendStreamingDelta, finalizeStreamingMessage, finalizeStreamingMessageAndAppendErrorNotice]
  );

  /**
   * 会話を初期状態に戻す（「新しい質問を始める」ボタンから呼ばれる）。
   * 進行中のリクエストがあればabortし、会話ID・メッセージ履歴・状態をすべてクリアする。
   */
  const resetConversation = useCallback(() => {
    abortControllerRef.current?.abort();
    conversationIdRef.current = null;
    setMessages([]);
    setStatus('initial');
    setErrorMessage('');
    setProgressLabel('');
  }, []);

  const hasStreamingMessage = messages.length > 0 && !!messages[messages.length - 1].streaming;

  return {
    messages,
    status,
    errorMessage,
    progressLabel,
    hasStreamingMessage,
    hint: isContextMissing ? CONTEXT_MISSING_HINT : (errorMessage || HINT_BY_STATUS[status]),
    isInputDisabled: isContextMissing || status === 'awaiting_response' || status === 'answered' || status === 'error',
    showResetButton: status === 'answered' || status === 'error',
    sendMessage,
    resetConversation,
  };
};

export default useAiChat;
